import moment from 'moment';
import { secureApi } from 'services/api';
import { MyQueryMeta } from 'services/api/getAxiosRTKBaseQuery';
import {
  IAcceptInvitation,
  Invitation,
  IResendInvitation,
  InvitationActionRequest,
} from 'services/api/invitations/types';

export const invitationsApi = secureApi.injectEndpoints({
  endpoints: builder => ({
    getInvitations: builder.query<
      {
        pendingReceivedInvitations: Invitation[];
        pendingSentInvitations: Invitation[];
      },
      void
    >({
      query: () => ({
        url: `/apiv2/user/invitations`,
        method: 'GET',
      }),
      transformResponse: (response: Invitation[]) => {
        const sortedInvitations = response.sort((a, b) =>
          moment(b.accessDetails.invitedAt).diff(a.accessDetails.invitedAt),
        );

        const pendingReceivedInvitations: Invitation[] = [];
        const pendingSentInvitations: Invitation[] = [];

        for (const invitation of sortedInvitations) {
          if (invitation.invitationType === 'received') {
            pendingReceivedInvitations.push(invitation);
          } else {
            pendingSentInvitations.push(invitation);
          }
        }

        return {
          pendingReceivedInvitations,
          pendingSentInvitations,
        };
      },
      providesTags: result =>
        result
          ? [
              ...result.pendingReceivedInvitations.map(
                ({ exposedAccessSharingRecordId: id }) => ({
                  id,
                  type: 'Invitation' as const,
                }),
              ),
              ...result.pendingSentInvitations.map(
                ({ exposedAccessSharingRecordId: id }) => ({
                  id,
                  type: 'Invitation' as const,
                }),
              ),
              'Invitation',
            ]
          : [],
    }),

    acceptInvitation: builder.mutation<void, IAcceptInvitation[]>({
      query: data => ({
        url: `/apiv2/user/invitations/accept`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Invitation', 'SharedItem'],
    }),

    resendInvitation: builder.mutation<Invitation, IResendInvitation>({
      query: data => ({
        url: `/apiv2/user/share`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: Invitation, meta, arg) => {
        const typedMeta = meta as MyQueryMeta;
        if (typedMeta?.status === 204) {
          // TODO: add translation
          throw new Error('Invitation not found');
        }
        return response;
      },
      invalidatesTags: ['Invitation'],
    }),

    revokeInvitation: builder.mutation<void, InvitationActionRequest[]>({
      query: data => ({
        url: `/apiv2/user/invitations/revoke`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: void, meta, arg) => {
        const typedMeta = meta as MyQueryMeta;
        if (typedMeta?.status === 204) {
          // TODO: add translation
          throw new Error('Invitation not found');
        }
        return response;
      },
      invalidatesTags: ['Invitation', 'SharedItem'],
    }),

    declineInvitation: builder.mutation<void, InvitationActionRequest[]>({
      query: data => ({
        url: `/apiv2/user/invitations/decline`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: void, meta, arg) => {
        const typedMeta = meta as MyQueryMeta;
        if (typedMeta?.status === 204) {
          // TODO: add translation
          throw new Error('Invitation not found');
        }
        return response;
      },
      invalidatesTags: ['Invitation', 'SharedItem'],
    }),
  }),
  overrideExisting: true,
});
